import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { NavigateFunction } from "react-router-dom";
import { CANVAS, type CanvasStateType } from "./types";

const canvasInitialState: CanvasStateType = {
	collection: {
		data: null,
		loading: true, // start with loading state to avoid flash
		error: null,
	},
	detail: {
		data: null,
		loading: false,
		submitting: false,
		error: null,
	},
	backgroundCollection: {
		data: null,
		loading: true, // start with loading state to avoid flash
		error: null,
	},
};

export const canvasSlice = createSlice({
	name: CANVAS,
	initialState: canvasInitialState,
	// Apply state changes
	reducers: {
		requestCreateCanvas: (
			canvas: CanvasStateType,
			{}: PayloadAction<{ data: Partial<Canvas>; navigate?: NavigateFunction }>,
		) => {
			canvas.detail.submitting = true;
		},
		receiveCreateCanvas: (canvas: CanvasStateType) => {
			canvas.detail.submitting = false;
		},
		failedCreateCanvas: (canvas: CanvasStateType, { payload: error }: PayloadAction<string>) => {
			canvas.detail.error = error;
			canvas.detail.submitting = false;
		},
		requestDeleteCanvas: (canvas: CanvasStateType, {}: PayloadAction<{ id: string }>) => {
			canvas.collection.loading = true;
		},
		receiveDeleteCanvas: (canvas: CanvasStateType, { payload }: PayloadAction<string>) => {
			if (canvas.collection.data) {
				canvas.collection.data = canvas.collection.data.filter((canvas) => canvas.id !== payload);
			}
			canvas.collection.loading = false;
		},
		failedDeleteCanvas: (canvas: CanvasStateType, { payload: error }: PayloadAction<string>) => {
			canvas.collection.error = error;
			canvas.collection.loading = false;
		},
		requestGetCanvases: (canvas: CanvasStateType) => {
			canvas.collection.loading = true;
		},
		receiveGetCanvases: (canvas: CanvasStateType, { payload }: PayloadAction<Canvas[]>) => {
			canvas.collection.loading = false;
			canvas.collection.data = payload;
		},
		failedGetCanvases: (canvas: CanvasStateType, { payload: error }: PayloadAction<string>) => {
			canvas.collection.loading = false;
			canvas.collection.error = error;
		},
		requestUpdateCanvas: (
			canvas: CanvasStateType,
			{}: PayloadAction<{ id: string; data: Partial<Canvas>; navigate?: NavigateFunction }>,
		) => {
			canvas.collection.loading = true;
		},
		receiveUpdateCanvas: (canvas: CanvasStateType) => {
			canvas.collection.loading = false;
		},
		failedUpdateCanvas: (canvas: CanvasStateType, { payload: error }: PayloadAction<string>) => {
			canvas.collection.error = error;
			canvas.collection.loading = false;
		},
		requestGetCanvasBackgrounds: (canvas: CanvasStateType) => {
			canvas.backgroundCollection.loading = true;
		},
		receiveGetCanvasBackgrounds: (
			canvas: CanvasStateType,
			{ payload }: PayloadAction<CanvasBackground[]>,
		) => {
			canvas.backgroundCollection.loading = false;
			canvas.backgroundCollection.data = payload;
		},
		failedGetCanvasBackgrounds: (
			canvas: CanvasStateType,
			{ payload: error }: PayloadAction<string>,
		) => {
			canvas.backgroundCollection.loading = false;
			canvas.backgroundCollection.error = error;
		},
	},
});

const actions = canvasSlice.actions;
const canvasReducer = canvasSlice.reducer;

export { actions, canvasReducer };
